// Library Methods
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useCallback, useContext, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import EditCommentModal from "../EditCommentModal/EditCommentModal";
import { EditIcon } from "src/assets/icons";

// Types
import { ExamCommentsType } from "src/services/exams/types";

// Utils
import {
  calculateWeekDifference,
  getLocalDate,
} from "src/utils/date/dateHelper";

// Constants
import { patientConditionTypes } from "src/constants/enums";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Hooks
import { useUpdateExamReportComment } from "src/services/exams/hooks/exam-reports/useUpdateExamReportComment";
import { useUpdateProgressionAnalysisComment } from "src/services/exams/hooks/progression-reports/useUpdateProgressionAnalysisComment";

type ReportCommentType = {
  comment: ExamCommentsType;
  prevConditions: Array<string>;
};

const ReportComment = ({ comment, prevConditions }: ReportCommentType) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { clinicInfo } = useClinicContext();

  const { sectionId } = useContext(ReportSectionContext);
  const { patient, exam, progressionAnalysis } = useContext(ReportContext);

  const { mutateAsync: updateExamReportComment } = useUpdateExamReportComment(
    clinicInfo?.id
  );
  const { mutateAsync: updateProgressionAnalysisComment } =
    useUpdateProgressionAnalysisComment(clinicInfo?.id);

  const [openEditModal, setOpenEditModal] = useState(false);

  const isProgressionAnalysis = useMemo(
    () =>
      location.pathname.includes("progression-analysis") ||
      location.search.includes("progression_analysis_id"),
    [location.pathname, location.search]
  );

  const sortedPatientConditions = useMemo(
    () =>
      patientConditionTypes
        .map((condition) => condition)
        .sort((a, b) => t(a).localeCompare(t(b))),
    [t]
  );

  const updateCommentWrapper = useCallback(
    async (commentData: {
      content: string;
      conditions?: string | undefined;
    }) => {
      if (isProgressionAnalysis && patient?.id && progressionAnalysis?.id) {
        return await updateProgressionAnalysisComment({
          patientId: patient?.id,
          progressionAnalysisId: progressionAnalysis?.id,
          commentId: comment.id,
          commentData,
        });
      } else if (!isProgressionAnalysis && patient?.id && exam?.id) {
        return await updateExamReportComment({
          patientId: patient?.id,
          examId: exam?.id,
          sectionId,
          commentId: comment.id,
          commentData,
        });
      }
    },
    [
      updateProgressionAnalysisComment,
      updateExamReportComment,
      isProgressionAnalysis,
      progressionAnalysis?.id,
      exam?.id,
      patient?.id,
      sectionId,
      comment.id,
    ]
  );

  const canEditComment = useMemo(
    () =>
      (calculateWeekDifference(comment?.creationDate, clinicInfo?.timezone) ??
        7) < 7,
    [comment?.creationDate, clinicInfo?.timezone]
  );

  const isEditedComment = useMemo(
    () =>
      comment?.creationDate?.slice(0, 10) !==
      comment?.lastModifiedDate?.slice(0, 10),
    [comment?.creationDate, comment?.lastModifiedDate]
  );

  const statusText = useMemo(
    () =>
      sortedPatientConditions
        .map((condition) => {
          // condition was added
          if (
            comment.conditions.includes(condition) &&
            !prevConditions.includes(condition)
          ) {
            return t("Status of {{ condition }} changed to be positive.", {
              condition: t(condition),
            });
          }
          // condition was removed
          else if (
            !comment.conditions.includes(condition) &&
            prevConditions.includes(condition)
          ) {
            return t("Status of {{ condition }} changed to be negative.", {
              condition: t(condition),
            });
          }

          return "";
        })
        .filter((val) => Boolean(val)),
    [comment.conditions, prevConditions, sortedPatientConditions, t]
  );

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", height: "fit-content" }}
    >
      {/* Creation date */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
          {getLocalDate(comment.creationDate, clinicInfo?.timezone)}
        </Typography>

        {isEditedComment && (
          <Typography sx={{ fontSize: "14px" }}>
            ({t("Edited")}{" "}
            {getLocalDate(comment.lastModifiedDate, clinicInfo?.timezone)})
          </Typography>
        )}

        {canEditComment && (
          <Box
            sx={{ cursor: "pointer" }}
            role="button"
            onClick={() => setOpenEditModal(true)}
          >
            <EditIcon width="20px" height="20px" />
          </Box>
        )}
      </Box>

      {/* Changes in status conditions */}
      {statusText.map((status, index) => (
        <Typography
          key={index}
          sx={{
            fontSize: "14px",
            color: "#5B6D7A",
            mt: index === 0 ? "4px" : "0px",
          }}
        >
          • {status}
        </Typography>
      ))}

      {/* Comment content */}
      <Typography sx={{ wordBreak: "break-all", mt: "4px", fontSize: "14px" }}>
        {comment.content}
      </Typography>

      <EditCommentModal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        commentToEdit={comment}
        updateCommentWrapper={updateCommentWrapper}
      />
    </Box>
  );
};

export default ReportComment;
